import {
  alpha,
  Box,
  Button,
  CircularProgress,
  EyebrowText,
  IconButton,
  Icons,
  iconSize,
  InputAdornment,
  Link,
  palette,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { BrandMark } from '../components/BrandMark';
import { AuthShell } from '../components/auth/AuthShell';
import { AuthErrorBanner, FieldLabel, fieldSx } from '../components/auth/authFields';
import { apiErrorStatus } from '../lib/mutation-toast';

const MIN_PASSWORD = 8;

type FieldErrors = { password?: string; confirm?: string };

/** What went wrong, by status: the link, the password, the rate limit, or the line. */
function resetErrorKey(status: number | null, err: unknown): string {
  if (status === 401) return 'gov.reset.invalidBody';
  if (status === 400) return 'gov.reset.errorRejected';
  if (status === 429) return 'gov.login.errorRateLimited';
  if (status === null && (err as { request?: unknown })?.request) return 'gov.login.errorNetwork';
  return 'gov.reset.errorServer';
}

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [linkRejected, setLinkRejected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // A link with no token can never succeed — show the recovery path straight away.
  if (!token) {
    return (
      <AuthShell top={<BrandMark />}>
        <InvalidLink />
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell top={<BrandMark />}>
        <Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5,
              color: palette.brand.accent,
              backgroundColor: palette.brand.accentTint,
              border: `1px solid ${alpha(palette.brand.accent, 0.25)}`,
            }}
          >
            <Icons.Check size={iconSize.xl} />
          </Box>
          <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
            {t('gov.reset.successTitle')}
          </Typography>
          <Typography
            sx={{ fontSize: 15, lineHeight: 1.55, color: palette.text.secondary, maxWidth: 380 }}
          >
            {t('gov.reset.successBody')}
          </Typography>
          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{ mt: 3.5, py: 1.4, borderRadius: '28px', fontSize: 15.5 }}
          >
            {t('gov.login.backToSignIn')}
          </Button>
        </Box>
      </AuthShell>
    );
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors: FieldErrors = {
      password: password.length < MIN_PASSWORD ? 'tooShort' : undefined,
      confirm: confirm !== password ? 'mismatch' : undefined,
    };
    setErrors(nextErrors);
    if (nextErrors.password || nextErrors.confirm) return;

    setError(null);
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      // Only a rejected token (401) means the link is spent; offering "request a
      // new link" for a rate limit or a dropped connection sends the officer to
      // redo a step that was never the problem.
      const status = apiErrorStatus(err);
      setLinkRejected(status === 401);
      setError(t(resetErrorKey(status, err)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell top={<BrandMark />}>
      <Box>
        <EyebrowText sx={{ color: palette.brand.accent, mb: 1 }}>
          {t('gov.reset.eyebrow')}
        </EyebrowText>
        <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
          {t('gov.reset.title')}
        </Typography>
        <Typography
          sx={{ fontSize: 15, lineHeight: 1.5, color: palette.text.secondary, maxWidth: 380 }}
        >
          {t('gov.reset.subtitle')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
        <Stack spacing={2.25}>
          {error && (
            <AuthErrorBanner>
              {error}{' '}
              {linkRejected && (
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="always"
                  sx={{ fontWeight: 600, color: 'inherit' }}
                >
                  {t('gov.reset.requestNew')}
                </Link>
              )}
            </AuthErrorBanner>
          )}

          <Box>
            <FieldLabel htmlFor="reset-password">{t('gov.reset.newPassword')}</FieldLabel>
            <TextField
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoFocus
              fullWidth
              placeholder={t('gov.login.passwordPlaceholder')}
              value={password}
              error={Boolean(errors.password)}
              helperText={errors.password ? t(`gov.reset.${errors.password}`) : undefined}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((p) => ({ ...p, password: undefined }));
                if (error) setError(null);
              }}
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Lock size={iconSize.lg} color={palette.text.muted} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={t(
                          showPassword ? 'gov.login.hidePassword' : 'gov.login.showPassword',
                        )}
                      >
                        {showPassword ? (
                          <Icons.EyeOff size={iconSize.lg} color={palette.text.muted} />
                        ) : (
                          <Icons.Eye size={iconSize.lg} color={palette.text.muted} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="reset-confirm">{t('gov.reset.confirmPassword')}</FieldLabel>
            <TextField
              id="reset-confirm"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              fullWidth
              placeholder={t('gov.login.passwordPlaceholder')}
              value={confirm}
              error={Boolean(errors.confirm)}
              helperText={errors.confirm ? t(`gov.reset.${errors.confirm}`) : undefined}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (errors.confirm) setErrors((p) => ({ ...p, confirm: undefined }));
              }}
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Lock size={iconSize.lg} color={palette.text.muted} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              py: 1.4,
              borderRadius: '28px',
              fontSize: 15.5,
              '&.Mui-disabled': {
                backgroundColor: alpha(palette.brand.accent, 0.7),
                color: palette.text.onDark,
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
            ) : (
              t('gov.reset.submit')
            )}
          </Button>
        </Stack>
      </Box>
    </AuthShell>
  );
}

function InvalidLink() {
  const { t } = useTranslation();
  return (
    <Box>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
          color: palette.semantic.warning,
          backgroundColor: alpha(palette.semantic.warning, 0.12),
          border: `1px solid ${alpha(palette.semantic.warning, 0.25)}`,
        }}
      >
        <Icons.AlertTriangle size={iconSize.xl} />
      </Box>
      <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
        {t('gov.reset.invalidTitle')}
      </Typography>
      <Typography
        sx={{ fontSize: 15, lineHeight: 1.55, color: palette.text.secondary, maxWidth: 380 }}
      >
        {t('gov.reset.invalidBody')}
      </Typography>
      <Button
        component={RouterLink}
        to="/forgot-password"
        variant="contained"
        fullWidth
        sx={{ mt: 3.5, py: 1.4, borderRadius: '28px', fontSize: 15.5 }}
      >
        {t('gov.reset.requestNew')}
      </Button>
    </Box>
  );
}
